import { Fragment, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { Formik, Form, Field } from 'formik'
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react'
import { ChevronDownIcon } from '@heroicons/react/20/solid'
import clsx from 'clsx'

import Modal from '../components/Modal'
import { DatePicker } from '../components/input/DatePicker'
import { IconEdit, IconPlus } from '../components/icons'
import { useRoles } from '../user/Auth'
import { PureEditor } from '../components/Editor'
import IpManagementBreadcrumbs from './IpManagementBreadcrumbs'
import { useIpStrategy, IpStrategy } from './IpStrategyProvider'

export default function ManagementStrategy() {
    const { t } = useTranslation()
    const { isEditUser } = useRoles()
    const { strategies, postStrategy, deleteStrategy } = useIpStrategy()

    const [selectedId, setSelectedId] = useState<number | undefined>(undefined)
    const [modalMode, setModalMode] = useState<null | 'add' | 'edit'>(null)
    const [isEditingContent, setIsEditingContent] = useState(false)
    const [draft, setDraft] = useState('')

    const selected = strategies.find(s => s.strategyId === selectedId) ?? strategies[0]

    async function addVersion({ version, versionDate, isCopy, copyFromId }: { version: string, versionDate: string, isCopy?: boolean, copyFromId?: number | string }) {
        const content = isCopy ? (strategies.find(s => s.strategyId === Number(copyFromId))?.content ?? '') : ''
        const saved = await postStrategy({ version, versionDate, content })
        setSelectedId(saved.strategyId)
        setModalMode(null)
    }

    async function updateVersion({ version, versionDate }: { version: string, versionDate: string }) {
        if (!selected) return
        await postStrategy({ ...selected, version, versionDate })
        setModalMode(null)
    }

    async function deleteVersion() {
        if (!selected) return
        await deleteStrategy(selected)
        const remaining = strategies.filter(s => s.strategyId !== selected.strategyId)
        setSelectedId(remaining[0]?.strategyId)
        setModalMode(null)
        setIsEditingContent(false)
    }

    function startEditingContent() {
        setDraft(selected?.content ?? '')
        setIsEditingContent(true)
    }

    async function saveContent() {
        if (!selected) return
        await postStrategy({ ...selected, content: draft })
        setIsEditingContent(false)
    }

    return (
        <>
            {/* @ts-ignore */}
            <Helmet>
                <title>{t('ip-strategy')} | Patent Cockpit</title>
            </Helmet>
            <div className="portfolio-menu max-w-4xl sm:items-center">
                <IpManagementBreadcrumbs label={t('ip-strategy')} />
            </div>
            <div className="main-content bg-pcx-100 pt-4">
                <div className="p-4 bg-white rounded-lg shadow max-w-4xl" id="ip-strategy-manager">
                    <div className="flex flex-row items-center gap-2 pb-4">
                        <h3 className="grow">{t('ip-strategy')}</h3>
                        {isEditUser && <button
                            type="button"
                            title={t('add-version')}
                            className="btn-secondary inline-flex items-center gap-1 py-0.5 px-2 text-sm"
                            onClick={() => setModalMode('add')}
                        >
                            <IconPlus className="h-4 w-4" /> {t('add-version')}
                        </button>}
                        {selected && <StrategyVersionDropdown
                            selected={selected}
                            strategies={strategies}
                            isEditUser={isEditUser}
                            onSelect={id => { setSelectedId(id); setIsEditingContent(false) }}
                            onEdit={() => setModalMode('edit')}
                        />}
                    </div>

                    {strategies.length === 0
                        ? <div className="text-slate-500 italic py-8 text-center">
                            {t('no-versions', { defaultValue: 'No versions yet. Add one to get started.' })}
                          </div>
                        : !selected
                        ? null
                        : isEditingContent
                        ? <div className="flex flex-col gap-2">
                            <div className="h-[28rem]">
                                <PureEditor key={selected.strategyId} content={selected.content} onUpdate={({ editor }) => setDraft(editor.getHTML())} />
                            </div>
                            <div className="flex flex-row-reverse gap-2">
                                <button type="button" className="btn-primary" onClick={saveContent}>{t('save')}</button>
                                <button type="button" className="btn-secondary" onClick={() => setIsEditingContent(false)}>{t('cancel')}</button>
                            </div>
                        </div>
                        : <div className="flex flex-row gap-2">
                            {selected.content?.trim()
                                ? <div className="grow prose max-w-none" dangerouslySetInnerHTML={{ __html: selected.content }} />
                                : <div className="grow text-slate-500 italic">
                                    {t('no-strategy-text', { defaultValue: 'No strategy described for this version yet.' })}
                                  </div>}
                            {isEditUser &&
                                <button type="button" title={t('edit')} className="btn-primary w-7 h-7 p-1 shrink-0" onClick={startEditingContent}>
                                    <IconEdit />
                                </button>}
                        </div>}
                </div>
            </div>

            {modalMode !== null &&
                <StrategyVersionModal
                    mode={modalMode}
                    strategy={modalMode === 'edit' ? selected : undefined}
                    strategies={strategies}
                    canDelete={modalMode === 'edit' && strategies.length > 1}
                    onSubmit={modalMode === 'edit' ? updateVersion : addVersion}
                    onDelete={deleteVersion}
                    onCancel={() => setModalMode(null)}
                />}
        </>
    )
}

function StrategyVersionDropdown({ selected, strategies, isEditUser, onSelect, onEdit }: {
    selected: IpStrategy,
    strategies: IpStrategy[],
    isEditUser: boolean,
    onSelect: (id: number) => void,
    onEdit: () => void,
}) {
    const { t } = useTranslation()

    const itemClassName = clsx(
        'hover:bg-pcx-600 hover:text-white text-pcx-500',
        'group flex whitespace-nowrap w-full items-center rounded-md px-2 py-2'
    )

    return (
        <Menu as="div" className="relative inline-block text-left text-sm">
            <div>
                <MenuButton className="btn-secondary inline-flex items-center gap-1 py-0.5 pl-2 pr-1">
                    <div>{`${t('version')}: ${selected.version} (${selected.versionDate})`}</div>
                    <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />
                </MenuButton>
            </div>
            <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
            >
                <MenuItems className="absolute right-0 mt-1 w-fit origin-top-right divide-y divide-gray-100 rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none z-10">
                    <div className="px-1 py-1">
                        {isEditUser && <MenuItem>
                            <button onClick={onEdit} className={itemClassName}>
                                <IconEdit className="mr-2 h-5 w-5" aria-hidden="true" /> {t('edit')}
                            </button>
                        </MenuItem>}
                        {strategies.map(s =>
                            <MenuItem key={s.strategyId}>
                                <button onClick={() => onSelect(s.strategyId!)} className={itemClassName}>
                                    <span className={isEditUser ? 'pl-7' : ''}>{`${t('version')} ${s.version} (${s.versionDate})`}</span>
                                </button>
                            </MenuItem>
                        )}
                    </div>
                </MenuItems>
            </Transition>
        </Menu>
    )
}

function StrategyVersionModal({ mode, strategy, strategies, canDelete, onSubmit, onDelete, onCancel }: {
    mode: 'add' | 'edit',
    strategy?: IpStrategy,
    strategies: IpStrategy[],
    canDelete: boolean,
    onSubmit: (values: { version: string, versionDate: string, isCopy?: boolean, copyFromId?: number | string }) => void,
    onDelete: () => void,
    onCancel: () => void,
}) {
    const { t } = useTranslation()
    const today = new Date().toISOString().slice(0, 10)

    const defaults = {
        version: strategy?.version ?? '',
        versionDate: strategy?.versionDate ?? today,
        isCopy: false,
        copyFromId: strategies[0]?.strategyId,
    }

    return (
        <Modal blurClick={onCancel} escAction={onCancel}>
            <Formik
                initialValues={defaults}
                onSubmit={onSubmit}
                validate={(values) => {
                    const errors: { version?: string, versionDate?: string } = {}
                    if (!values.version?.trim()) errors.version = t('is-required')
                    else if (!values.versionDate?.trim()) errors.versionDate = t('is-required')
                    return errors
                }}
            >{({ values, touched, errors, dirty }) =>
                <Form>
                    <div className="flex flex-col p-4 gap-2 sm:min-w-[24rem]">
                        <h4>{mode === 'edit'
                            ? t('edit-version', { defaultValue: 'Edit version' })
                            : t('add-version')}</h4>
                        <label className="w-full">
                            <div className="label mb-1">{t('version')} <span className="text-red-700">{touched.version && errors.version}</span></div>
                            <Field className="form-input w-full" name="version" autoFocus />
                        </label>
                        <label className="w-full">
                            <div className="label mb-1">{t('versionDate')} <span className="text-red-700">{touched.versionDate && errors.versionDate}</span></div>
                            <Field className="form-input py-1 w-full" name="versionDate" as={DatePicker} />
                        </label>
                        {mode === 'add' && strategies.length > 0 && <>
                            <label className="w-fit pt-2 flex flex-row items-center gap-2">
                                <Field className="form-checkbox" name="isCopy" type="checkbox" /> <span className="label normal-case">{t('create-copy')}</span>
                            </label>
                            {values.isCopy && <label className="w-full">
                                <div className="label mb-1">{t('copy-from')}</div>
                                <Field className="form-select w-full" name="copyFromId" as="select">
                                    {strategies.map(s =>
                                        <option key={s.strategyId} value={s.strategyId}>{`${t('version')} ${s.version} (${s.versionDate})`}</option>)}
                                </Field>
                            </label>}
                        </>}
                    </div>
                    <div className="p-4 bg-pcx-200 flex flex-row-reverse gap-4">
                        <input className="btn-primary" type="submit" value={t('save')} />
                        <button type="button" className="btn-secondary" onClick={onCancel}>{t('cancel')}</button>
                        {mode === 'edit' && <>
                            <div className="grow" />
                            <button
                                type="button"
                                className="btn-warn disabled:btn-disabled"
                                disabled={dirty || !canDelete}
                                title={!canDelete ? t('cannot-delete-last-version', { defaultValue: 'The last version cannot be deleted.' }) : undefined}
                                onClick={onDelete}
                            >{t('delete-version', { defaultValue: 'Delete version' })}</button>
                        </>}
                    </div>
                </Form>
            }</Formik>
        </Modal>
    )
}
